<template>
    <v-card outlined class="cuentas fill-height">
        <div class="d-flex align-center flex-wrap px-4 pt-3">
            <span class="cuentas__title">{{ title }}</span>
            <FilterBadges :badges="filterBadges" class="ml-2"></FilterBadges>
            <v-spacer></v-spacer>
            <CurrencyToggle v-model="selected" :codes="codes"></CurrencyToggle>
        </div>
        <v-card-text>
            <StatCard :tone="tone" :icon="icon" :title="totalTitle" :value="totalValue" :description="totalHint"
                :loading="loading"></StatCard>
            <p v-if="error" class="cuentas__error" role="alert">{{ error }}</p>
            <v-simple-table v-else-if="rows.length" dense class="cuentas__table mt-3">
                <thead>
                    <tr>
                        <th class="text-left">{{ nameHeader }}</th>
                        <th class="text-right">Saldo</th>
                        <th class="text-right">Docs.</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in rows" :key="row.id + '-' + row.nombre">
                        <td class="cuentas__name" :title="row.nombre">{{ row.nombre }}</td>
                        <td class="text-right">{{ money(row.saldo) }}</td>
                        <td class="text-right">{{ row.documentos }}</td>
                    </tr>
                </tbody>
            </v-simple-table>
            <p v-else-if="!loading" class="cuentas__empty">Sin saldos pendientes</p>
        </v-card-text>
    </v-card>
</template>
<script>
import StatCard from '@/components/shared/StatCard.vue';
import DashboardDataService from '@/service/dashboard/DashboardDataService';
import { dashboardFilterMixin, dashboardWidgetMixin } from '@/mixins/dashboardWidgetMixin';
import CurrencyToggle from './CurrencyToggle.vue';
import FilterBadges from './FilterBadges.vue';
import { currencyCodes, formatMoney, forCurrency, pickCurrency, summaryFor } from './dashboardData';

const KINDS = {
    // Lo que se debe a proveedores no se reparte por sucursal: `pagar` ignora los filtros.
    cobrar: { load: (filters) => DashboardDataService.porCobrar(5, filters), totalTitle: 'Total por cobrar', nameHeader: 'Cliente' },
    pagar: { load: () => DashboardDataService.porPagar(5), totalTitle: 'Total por pagar', nameHeader: 'Proveedor' }
};

/**
 * Saldos abiertos: lo que los clientes deben (`por-cobrar`) o lo que se debe a proveedores (`por-pagar`), con el total
 * y los 5 mayores. Ambos son de empresa: solo los pide quien tiene `CAP.DASHBOARD_TODAS_SUCURSALES_VER` (el backend
 * responde 403 al resto).
 */
export default {
    name: 'CuentasPendientes',
    components: { CurrencyToggle, FilterBadges, StatCard },
    mixins: [dashboardWidgetMixin, dashboardFilterMixin],
    // Es un saldo a hoy: el producto no lo divide (el saldo es por ticket). Por cobrar sí sigue la sucursal; por pagar no
    // (la deuda es con proveedores, sin sucursal ni producto).
    filterScope() {
        const byTicket = 'El saldo es por ticket y un ticket mezcla productos.';
        return this.kind === 'cobrar'
            ? { sucursal: true, producto: false, reasons: { producto: byTicket } }
            : {
                sucursal: false,
                producto: false,
                reasons: {
                    sucursal: 'Lo que se debe a proveedores no se reparte por sucursal.',
                    producto: 'La deuda es por entrada de compra, no por producto.'
                }
            };
    },
    props: {
        title: { type: String, required: true },
        kind: { type: String, required: true, validator: (kind) => Object.keys(KINDS).includes(kind) },
        icon: { type: String, default: '' },
        tone: { type: String, default: 'warning' }
    },
    data() {
        return { data: null, selected: '' };
    },
    computed: {
        totals() {
            return this.data ? this.data.totales : [];
        },
        codes() {
            return currencyCodes(this.totals);
        },
        currency() {
            return pickCurrency(this.codes, this.selected);
        },
        summary() {
            return summaryFor(this.totals, this.currency);
        },
        rows() {
            return forCurrency(this.data && this.data.top, this.currency);
        },
        totalTitle() {
            return KINDS[this.kind].totalTitle;
        },
        nameHeader() {
            return KINDS[this.kind].nameHeader;
        },
        totalValue() {
            return this.error ? null : formatMoney(this.summary.owed, this.currency);
        },
        totalHint() {
            // Saldo abierto a hoy, de cualquier fecha: no sigue el filtro de fechas del tablero.
            return this.error ? '' : `${this.summary.tickets} documentos · a hoy, sin filtro de fechas`;
        }
    },
    created() {
        this.reload();
    },
    methods: {
        money(value) {
            return formatMoney(value, this.currency);
        },
        reload() {
            return this.loadWidget(() => KINDS[this.kind].load(this.filters), (data) => {
                this.data = data;
            });
        }
    }
};
</script>
<style scoped>
/* Sin clases `text-*` de Vuetify en títulos: main.css les cambia la fuente. */
.cuentas__title {
    font-size: 1rem;
    font-weight: 700;
}
.cuentas__table {
    font-variant-numeric: tabular-nums;
}
.cuentas__name {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.cuentas__empty {
    margin: 12px 0 0;
    font-size: 13px;
    color: var(--app-text-muted);
}
.cuentas__error {
    margin: 12px 0 0;
    font-size: 13px;
    color: var(--v-error-base);
}
</style>
